import React, {useState, useEffect, useCallback} from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Alert,
} from '@mui/material';
import {useAuthFrontendApis} from '@propelauth/frontend-apis-react';
import Loading from '../../components/Loading';
import {SettingsCard} from '../../components/SettingsCard';
import PropTypes from 'prop-types';

const styles = {
  cardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '24px',
    color: '#162523',
  },
  tableContainer: {
    boxShadow: 'none',
    '& .MuiTable-root': {
      borderCollapse: 'separate',
    },
  },
  tableHeader: {
    backgroundColor: 'transparent',
    '& .MuiTableCell-head': {
      color: '#819190',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '150%',
      letterSpacing: '-0.14px',
      borderBottom: '1px solid #E1E5E4',
      padding: '16px 24px',
      backgroundColor: 'transparent',
    },
  },
  tableRow: {
    backgroundColor: 'transparent !important',
    '&:nth-of-type(odd)': {
      backgroundColor: 'transparent !important',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'transparent !important',
    },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #E1E5E4',
      padding: '16px 24px',
      backgroundColor: 'transparent !important',
    },
    '&:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },
  userName: {
    color: '#000',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.14px',
  },
  statusText: {
    color: '#162523',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.14px',
  },
  revokeButton: {
    color: '#E44F40',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '130%',
    letterSpacing: '-0.14px',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '16px',
    '& .MuiPagination-ul': {
      '& .MuiPaginationItem-root': {
        color: '#819190',
        '&.Mui-selected': {
          backgroundColor: '#26A993',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#26A993',
          },
        },
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
      },
    },
  },
};

// Custom hook for fetching pending invites
const usePendingInvites = (orgId) => {
  const {fetchPendingOrgInvites, revokeUserOrgInvitation} =
    useAuthFrontendApis();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchPendingInvites = useCallback(
    async (page = 0, pageSize = 25, search = '') => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchPendingOrgInvites(orgId, {
          page_number: page,
          page_size: pageSize,
          email_search: search,
        });

        await response.handle({
          success: (responseData) => {
            setData(responseData);
          },
          orgNotFound: () => {
            setError('Organization not found');
          },
          orgsNotEnabled: () => {
            setError('Organizations are not enabled');
          },
          unauthorized: () => {
            setError('You are not authorized to view this organization');
          },
          unexpectedOrUnhandled: (err) => {
            setError(err.user_facing_error || 'An unexpected error occurred');
          },
        });
      } catch (err) {
        setError('Failed to fetch pending invitations');
      } finally {
        setIsLoading(false);
      }
    },
    [orgId, fetchPendingOrgInvites]
  );

  const revokeInvite = useCallback(
    async (email, onShowSnackbar, onInviteRevoked) => {
      try {
        const response = await revokeUserOrgInvitation({
          org_id: orgId,
          email,
        });

        await response.handle({
          success: () => {
            onShowSnackbar({
              message: `Invitation to ${email} has been revoked`,
              severity: 'success',
            });
            // Refresh the invites list
            fetchPendingInvites();
            // Notify parent to refresh pending invites check
            if (onInviteRevoked) {
              onInviteRevoked();
            }
          },
          badRequest: (error) => {
            const errorMessages = Object.values(
              error.user_facing_errors || {}
            ).join(', ');
            onShowSnackbar({
              message: `Error revoking invitation: ${errorMessages}`,
              severity: 'error',
            });
          },
          orgNotFound: () => {
            onShowSnackbar({
              message: 'Organization not found',
              severity: 'error',
            });
          },
          orgsNotEnabled: () => {
            onShowSnackbar({
              message: 'Organizations are not enabled',
              severity: 'error',
            });
          },
          noRevokeInvitePermission: () => {
            onShowSnackbar({
              message: 'You do not have permission to revoke invitations',
              severity: 'error',
            });
          },
          unexpectedOrUnhandled: (err) => {
            onShowSnackbar({
              message: err.user_facing_error || 'Failed to revoke invitation',
              severity: 'error',
            });
          },
        });
      } catch (err) {
        onShowSnackbar({
          message: 'Failed to revoke invitation',
          severity: 'error',
        });
      }
    },
    [orgId, revokeUserOrgInvitation, fetchPendingInvites]
  );

  return {
    isLoading,
    error,
    data,
    fetchPendingInvites,
    revokeInvite,
  };
};

const CompanyPendingInvitations = ({
  company,
  debouncedSearch,
  onShowSnackbar,
  onInviteRevoked,
  refreshTrigger,
}) => {
  const {isLoading, error, data, fetchPendingInvites, revokeInvite} =
    usePendingInvites(company.orgId);

  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Fetch pending invites when page, search, or refreshTrigger changes
  useEffect(() => {
    fetchPendingInvites(page - 1, pageSize, debouncedSearch);
  }, [page, debouncedSearch, fetchPendingInvites, refreshTrigger]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRevokeInvite = useCallback(
    (email) => {
      if (
        window.confirm(
          `Are you sure you want to revoke the invitation to ${email}?`
        )
      ) {
        revokeInvite(email, onShowSnackbar, onInviteRevoked);
      }
    },
    [revokeInvite, onShowSnackbar, onInviteRevoked]
  );

  const totalPages = data ? Math.ceil(data.total_count / pageSize) : 0;

  return (
    <SettingsCard sx={{marginBottom: '24px'}}>
      <Typography sx={styles.cardTitle}>Pending invitations</Typography>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{marginBottom: '16px'}}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
          <Loading
            loading={true}
            message="Loading invitations..."
            containerProps={{
              height: '200px',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 'none',
            }}
          />
        </Box>
      )}

      {/* Invitations Table */}
      {!isLoading && !error && (
        <>
          <TableContainer sx={styles.tableContainer}>
            <Table>
              <TableHead sx={styles.tableHeader}>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Revoke Invite</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.pending_invites?.length === 0 ? (
                  <TableRow sx={styles.tableRow}>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No pending invitations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.pending_invites?.map((invitation) => (
                    <TableRow key={invitation.email} sx={styles.tableRow}>
                      <TableCell>
                        <Typography sx={styles.userName}>
                          {invitation.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={styles.statusText}>
                          {invitation.role}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={styles.statusText}>
                          {new Date(
                            invitation.expires_at_seconds * 1000
                          ).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={styles.revokeButton}
                          onClick={() => handleRevokeInvite(invitation.email)}
                        >
                          Revoke
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Invitations Pagination */}
          {totalPages > 1 && (
            <Box sx={styles.paginationContainer}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </SettingsCard>
  );
};

CompanyPendingInvitations.propTypes = {
  company: PropTypes.shape({
    orgId: PropTypes.string.isRequired,
    orgName: PropTypes.string.isRequired,
  }).isRequired,
  debouncedSearch: PropTypes.string.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
  onInviteRevoked: PropTypes.func,
  refreshTrigger: PropTypes.number,
};

export default CompanyPendingInvitations;
